import { defineStore, type Store } from "pinia";
import joi from "joi";
import { useAuthStore } from "@/stores/authStore";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
  IInstitution,
} from "@/interfaces";
import type {
  EditorModel,
  PreviewBasedState,
  StoreName,
  TabErrorSchemaTypes,
} from "@/types";
import helpers from "@/helpers";
import type { EmployeeInput } from "@/graphql/generated-types";
import type { InstitutionsEmployeeOnly } from "@/graphql";
import api from "@/api";
import { TRANSLATED_VALIDATION_MESSAGES } from "@/helpers/forms/validate";
import {
  PICTURES_FEATURE,
  type PicturesFeatureState,
} from "@/helpers/features/PicturesFeature";
import toPreviewBasedState from "@/helpers/shared/toPreviewBasedState";

const BASE_VALIDATION_SCHEMA = {
  name: joi.string().max(50).required(),
  surname: joi.string().max(50).required(),
  alias: joi.string().max(50).optional().allow(""),
  description: joi.string().max(2000).optional().allow(""),
  email: joi
    .string()
    .email({ tlds: false })
    .required()
    .custom((value, helper) => {
      const emailPattern = /^[a-zA-Z0-9_.\-+]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]+$/;

      if (!emailPattern.test(value as string)) {
        return helper.error("email.special_character");
      }

      return true;
    }),
  imageList: joi.array().min(1).max(1),
};

const VALIDATION_SCHEMA_UPDATE = joi.object({
  ...BASE_VALIDATION_SCHEMA,
});

const VALIDATION_SCHEMA_CREATE = joi.object({
  ...BASE_VALIDATION_SCHEMA,
  password: joi
    .string()
    .required()
    .custom((value, helper) => {
      const { passwordRetry } = helper.state.ancestors[0];
      const { isValid } = helpers.forms.isValidPassword(
        value as string,
        passwordRetry,
      );

      if (isValid) {
        return true;
      }

      return helper.error("custom.password_not_acceptable");
    }),
});

type ValidationSchemaTypes = "update" | "create";

const VALIDATION_SCHEMA_MAP: Record<ValidationSchemaTypes, joi.ObjectSchema> = {
  update: VALIDATION_SCHEMA_UPDATE,
  create: VALIDATION_SCHEMA_CREATE,
};

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["name", "surname", "alias", "phone", "mobile"],
  pictures: ["imageList"],
  security: ["email"],
  login: ["email", "password"],
};

export type EmployeeItem = {
  id?: string;
  name: string;
  surname: string;
  firstName: string;
  email: string;
  displayName: string;
  lastName: string;
  mobile: string;
  phone: string;
  title: string;
  type: string;
  alias: string;
  password: string;
  passwordRetry: string;
  description: EditorModel;
  showImage: boolean;
  isSuperuser: boolean;
  institution?: IInstitution;
  userId?: string;
  employeeTitleId?: string;
} & PicturesFeatureState;

export type EmployeeListFilters = {
  titles: PreviewBasedState<string[]>;
};

export interface IEmpoyeesState extends IDefaultState {
  listFilters: EmployeeListFilters;
  item: EmployeeItem;
}

interface EmployeeStoreActions extends IDefaultStoreActions {
  isEmailValid(): Promise<boolean>;
}

export type EmployeesStore = Store<
  string,
  IEmpoyeesState,
  IDefultStoreGetters<IEmpoyeesState>,
  EmployeeStoreActions
>;

const getDefaultListFilters = (): EmployeeListFilters => {
  return {
    titles: toPreviewBasedState([]),
  };
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IEmpoyeesState => ({
      tabErrors: {},
      originalHash: null,
      validation: null,
      item: {
        name: "",
        surname: "",
        firstName: "",
        email: "",
        displayName: "",
        lastName: "",
        mobile: "",
        phone: "",
        title: "",
        type: "",
        alias: "",
        password: "",
        passwordRetry: "",
        description: helpers.quill.getEmptyEditorModel(),
        showImage: false,
        isSuperuser: false,
        ...PICTURES_FEATURE.getDefault(),
      },
      listFilters: getDefaultListFilters(),
    }),
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash() {
        const item = {
          ...this.getItem(),
          ...PICTURES_FEATURE.hash(this.item),
        };
        return md5(JSON.stringify(item));
      },
      setOriginalHash() {
        // It can take times to TextEditorInput convert html to delta.
        // That's why there is a timeout here.
        const timeout = setTimeout(() => {
          this.originalHash = this.currentHash();
          clearTimeout(timeout);
        }, 10);
      },
      async setItem(data: InstitutionsEmployeeOnly) {
        this.item = {
          id: data.id,
          firstName: data.firstName || "",
          displayName: data.displayName,
          title: data.title || "",
          type: data.type,
          password: "",
          passwordRetry: "",
          showImage: data.showImage || false,
          description: helpers.quill.htmlToEditorModel(data.description || ""),
          lastName: data.lastName || "",
          alias: data.alias || "",
          phone: data.phone || "",
          mobile: data.mobile || "",
          email: data.user?.email || data.user?.username || "",
          name: data.name || "",
          surname: data.surname || "",
          isSuperuser: data.isSuperuser || false,
          userId: data.user?.id,
          employeeTitleId: data.employeeTitle?.id,
          ...PICTURES_FEATURE.getState(data),
        };

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): EmployeeInput {
        const item = { ...this.item };
        const authStore = useAuthStore();

        return {
          id: item.id?.toString(),
          description: helpers.quill.editorModelToHtml(item.description),
          firstname: item.name,
          lastname: item.surname,
          mobile: item.mobile,
          phone: item.phone,
          alias: item.alias,
          institution: (
            item.institution?.id || authStore.institutionId
          ).toString(),
          showImage: item.showImage,
          showEmail: true,
          showMobile: true,
          employeeTitle: item.employeeTitleId || null,
        };
      },
      async isNotValid(schema: ValidationSchemaTypes = "update") {
        const validationSchema = VALIDATION_SCHEMA_MAP[schema];
        const data = {
          ...this.item,
          description: helpers.quill.editorModelToText(this.item.description),
          ...PICTURES_FEATURE.toValidation(this.item),
        };
        this.validation = helpers.forms.validate(validationSchema, data);
        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        if (this.validation.isValid) {
          await this.isEmailValid();
        }

        return !this.validation.isValid;
      },
      async isEmailValid() {
        const id = await api.user.getIdByEmailInUse(this.item.email);
        if (id) {
          // Should be different the current userId (if this is an update)
          if (!this.item.userId || this.item.userId !== id) {
            this.validation = {
              ...this.validation,
              isValid: !id,
              errors: {
                email: TRANSLATED_VALIDATION_MESSAGES["email.used"]!,
              },
            };
          }
        }

        return !id;
      },
    },
  });
};

// News View store
export const useEmployeesStore = createUseStore("EMPLOYEES");

// We use the same store structure for the create modal.
export const useAddEmployeeStore = createUseStore("ADD_EMPLOYEES");
